// "This assessment ends in N days" — the notice shown at the top of the Add
// Candidate drawer, and the day arithmetic behind it.
//
// Adding a candidate to an assessment that is about to close hands them a
// window measured in hours, which the admin usually did not intend and could
// not see from the drawer. Rather than inventing a per-candidate deadline
// (invisible on every assessment-level screen), the drawer states how long is
// left and offers to move the assessment's OWN end date, so everyone on it
// moves together.
//
// ── Timezone, read this before touching the arithmetic ───────────────────────
// assessment_institute_map / assessment_corporate_map start_time and end_time
// store IST WALL-CLOCK DIGITS AS UTC, not the true instant — which is why the
// tables render them with `timeZone: 'UTC'`. So "today" has to be built in that
// same frame (now + IST offset, read back through UTC getters) before the two
// can be compared. Using local midnight here puts the count a day out for every
// admin west of IST, and for IST itself after 18:30 UTC.

const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000
const MS_PER_DAY = 24 * 60 * 60 * 1000

const utcMidnight = date =>
  Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate())

/**
 * Whole calendar days from today (IST) to the assessment's end date.
 * 0 = ends today, 1 = tomorrow, negative = already closed, null = no usable date.
 *
 * Counted in calendar days rather than elapsed hours because that is what the
 * sentence says: an assessment ending tomorrow at 09:00 still "ends tomorrow",
 * not "in 0 days".
 */
function daysUntilEnd(endDate, now = new Date()) {
  if (!endDate) return null
  const end = endDate instanceof Date ? endDate : new Date(endDate)
  if (Number.isNaN(end.getTime())) return null
  const istNow = new Date(now.getTime() + IST_OFFSET_MS)
  return Math.round((utcMidnight(end) - utcMidnight(istNow)) / MS_PER_DAY)
}

/** Inclusive calendar dates available between the later of today/start and end. */
function availableDays(startDate, endDate, now = new Date()) {
  const remainingDays = daysUntilEnd(endDate, now)
  if (remainingDays === null || remainingDays < 0) return remainingDays
  if (!startDate) return remainingDays

  const start = startDate instanceof Date ? startDate : new Date(startDate)
  if (Number.isNaN(start.getTime())) return remainingDays
  const istNow = new Date(now.getTime() + IST_OFFSET_MS)
  const effectiveStart = Math.max(utcMidnight(start), utcMidnight(istNow))
  return Math.round((utcMidnight(new Date(endDate)) - effectiveStart) / MS_PER_DAY) + 1
}

/** The stored digits are IST wall clock, so render them in UTC. */
function formatEndDate(endDate) {
  if (!endDate) return ''
  const end = endDate instanceof Date ? endDate : new Date(endDate)
  if (Number.isNaN(end.getTime())) return ''
  return end.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  })
}

/**
 * The notice to show, or null when there is no end date to talk about.
 *
 * `tone` maps to an antd Alert type. The thresholds are deliberately blunt:
 * closed is an error because candidates added now cannot attempt at all, and
 * a week is the point where an admin would still want to be asked.
 *
 * @returns {{tone: string, headline: string, detail: string, days: number}|null}
 */
function buildEndDateNotice(endDate, now = new Date(), startDate) {
  const days = daysUntilEnd(endDate, now)
  const daysAvailable = availableDays(startDate, endDate, now)
  if (days === null) return null
  const on = formatEndDate(endDate)

  if (days < 0) {
    return {
      tone: 'error',
      days,
      daysAvailable,
      headline: `This assessment closed on ${on}.`,
      detail:
        'Candidates you add now will not be able to attempt it until you extend the end date.',
    }
  }
  if (days === 0) {
    return {
      tone: 'error',
      days,
      daysAvailable,
      headline: `This assessment closes today (${on}).`,
      detail: 'Candidates you add now get the rest of today to finish.',
    }
  }
  if (days === 1) {
    return {
      tone: 'warning',
      days,
      daysAvailable,
      headline: startDate
        ? `This assessment has ${daysAvailable} day of validity and closes on ${on}.`
        : `This assessment closes tomorrow (${on}).`,
      detail: startDate
        ? 'Candidates you add now get 1 day to finish.'
        : 'Candidates you add now get until tomorrow to finish.',
    }
  }
  if (days <= 7) {
    return {
      tone: 'warning',
      days,
      daysAvailable,
      headline: startDate
        ? `This assessment has ${daysAvailable} days of validity and closes on ${on}.`
        : `This assessment closes in ${days} days (${on}).`,
      detail: `Candidates you add now get ${daysAvailable} days to finish.`,
    }
  }
  return {
    tone: 'info',
    days,
    daysAvailable,
    headline: startDate
      ? `This assessment has ${daysAvailable} days of validity and closes on ${on}.`
      : `This assessment closes in ${days} days (${on}).`,
    detail: `Candidates you add now get ${daysAvailable} days to finish.`,
  }
}

module.exports = {
  IST_OFFSET_MS,
  daysUntilEnd,
  availableDays,
  formatEndDate,
  buildEndDateNotice,
}
